import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  TextField,
  Paper,
  Modal, Box,
  Typography,
  AppBar,
  Toolbar,
} from "@mui/material";
import LoadingBar from "react-top-loading-bar";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EditMainCategory = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { mainCategoryId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState({
    mainCategoryName: "",
    description: "",
  });
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch category details by ID
  const fetchCategory = async () => {
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/inventory/Categories/GetMainCategory`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({}),
        }
      );

      if (response.ok) {
        const categories = await response.json();
        const categoryData = categories.find(
          (cat) => cat.mainCategoryId === parseInt(mainCategoryId)
        );
        if (categoryData) {
          setCategory({
            mainCategoryName: categoryData.mainCategoryName || "",
            description: categoryData.description || "",
          });
        } else {
          setError("Category not found");
        }
      } else {
        setError("Failed to fetch category details");
      }
    } catch (error) {
      console.error("Error fetching category:", error);
      setError("An error occurred while fetching the category");
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategory((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/inventory/Categories/Id?Id=${mainCategoryId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({
            mainCategoryName: category.mainCategoryName,
            description: category.description,
          }),
        }
      );

      if (response.ok) {
        setShowSuccessModal(true);
      } else {
        // alert("Failed to update category");
        setSnackbarMessage('Failed to update category');
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
      }
    } catch (error) {
      console.error("Error updating category:", error);
      // alert("An error occurred while updating the category");
      setSnackbarMessage('An error occurred while updating the category');
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
    } finally {
      setLoadingBarProgress(100);
    }
  };

  useEffect(() => {
    fetchCategory();
  }, [mainCategoryId]);

  return (
    <div className="container mt-4">
      <LoadingBar
        progress={loadingBarProgress}
        color="rgb(152, 106, 182)"
        height={5}
      />
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Edit Main Category
          </Typography>
        </Toolbar>
      </AppBar>

      <Paper elevation={3} style={{ padding: 16, paddingTop: 29, marginTop: 6 }}>
        {isLoading ? (
          <Typography>Loading...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <form onSubmit={handleSubmit}>

            <TextField
              label="Category Name"
              name="mainCategoryName"
              value={category.mainCategoryName}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              required
              variant="outlined"
            />
            <TextField
              label="Description"
              name="description"
              value={category.description}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              multiline
              rows={4}
              variant="outlined"
            />
            <div style={{ marginTop: 16 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                style={{
                  marginRight: 8, width: "80px",
                  height: "50px",
                  padding: "10px 0",
                  fontWeight: 'bold'
                }}
              >
                UPDATE
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => navigate(-1)}
                style={{
                  width: "80px",
                  height: "50px",
                  padding: "10px 0",
                  fontWeight: 'bold'
                }}
              >
                CANCEL
              </Button>
            </div>
          </form>
        )}
      </Paper>


      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Category Updated ✅
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              The Category has been Updated successfully.
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1); // go back after closing modal
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

    </div>
  );
};

export default EditMainCategory;
